import { CircularProgress } from '@material-ui/core';
import { Button, Grid } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import PageLayout from '../../../common/layouts/pageLayout';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { exportFatura, fetchClientsNome, fetchFollowUpsPorClientes } from './ocrExportHelpers';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { selectUnidadeSelecionada } from '../../../../features/usuario-logado/usuarioLogadoSlice';
import { KEYCLOAK_TOKEN_TIMEOUT } from '../../../../App';
import { useUnidadeNegocioGuard } from '../../../common/hooks/useUnidadeNegocioGuard';

const OcrExportPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useUnidadeNegocioGuard();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;

  // garante refresh do token
  useEffect(() => {
    keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedUnit = useSelector(selectUnidadeSelecionada);

  const [followups, setFollowups] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedFollowups, setSelectedFollowups] = useState<SimpleMultiSelectOption[]>([]);
  const [isLoadingFollowups, setIsLoadingFollowups] = useState(false);

  const [clients, setClients] = useState<SimpleMultiSelectOption[]>([]);
  const [selectedClients, setSelectedClients] = useState<SimpleMultiSelectOption[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchClients('a');
  }, []);

  const fetchClients = useCallback(
    async (inputValue: string) => {
      if (!token || !selectedUnit) {
        dispatch(setErrorFeedback({ message: 'Token ou unidade não disponível.' }));
        return;
      }
      try {
        setIsLoadingClients(true);
        const clientOptions = await fetchClientsNome(inputValue, token, selectedUnit);
        setClients(clientOptions);
      } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch(setErrorFeedback({ message: 'Erro ao buscar clientes', details: (error as any).message }));
      } finally {
        setIsLoadingClients(false);
      }
    },
    [dispatch, token, selectedUnit]
  );

  // 🔑 chave derivada só com os nomes/valores — garante trigger do useEffect
  const clientsKey = useMemo(() => {
    const names = selectedClients.map((o) => String(o.value)).sort();
    return names.join('||');
  }, [selectedClients]);

  // 🔁 sempre que a VARIÁVEL selectedClients mudar, buscamos followups
  useEffect(() => {
    const carregarFollowups = async () => {
      if (!token || !selectedUnit) return;

      const nomesClientes = clientsKey ? clientsKey.split('||') : [];
      if (!nomesClientes.length) {
        setFollowups([]);
        setSelectedFollowups([]);
        return;
      }

      try {
        setIsLoadingFollowups(true);
        const fu = await fetchFollowUpsPorClientes(nomesClientes, token, selectedUnit);
        setFollowups(fu);
        setSelectedFollowups([]); // opcional: limpar seleção anterior
      } catch (e: any) {
        dispatch(setErrorFeedback({ message: 'Erro ao buscar followups', details: e.message }));
      } finally {
        setIsLoadingFollowups(false);
      }
    };

    carregarFollowups();
    // dependemos de clientsKey (derivada da variável selectedClients), token e unidade
  }, [clientsKey, token, selectedUnit, dispatch]);

  const onSubmit = async () => {
    if (!token || !selectedUnit) {
      dispatch(setErrorFeedback({ message: 'Token ou unidade não disponível.' }));
      return;
    }
    try {
      const followupIds = selectedFollowups.map((opt) => Number(opt.id)); // IDs!
      const selectedClientNames = selectedClients.map((opt) => String(opt.value));
      const message: string | null = await exportFatura(followupIds, selectedClientNames, token, selectedUnit);
      if (message != null) {
        dispatch(setErrorFeedback({ message }));
      } else {
        dispatch(
          setSuccessFeedback({
            message: t('com.muralis.qcx.integracao.Planilhas.sucesso.planilhaComSuceso'),
          })
        );
      }
    } catch (error) {
      console.error('Error while exporting fatura:', error);
      dispatch(setErrorFeedback({ message: 'Erro ao exportar fatura', details: (error as any).message }));
    }
  };

  return (
    <PageLayout title={'EXPORTAR FATURA OCR'} icon={<PreviewIcon color={'secondary'} />}>
      <Form onSubmit={onSubmit}>
        {({ handleSubmit, submitting }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <h2>Informações</h2>
                </Grid>
                <Grid item xs={4}>
                  {isLoadingClients ? (
                    <CircularProgress size={24} />
                  ) : (
                    <MultiSelectStyled
                      options={clients}
                      placeholder="Digite o nome do cliente para ver as opções"
                      label={t('Selecione os Clientes')}
                      controlledValues={selectedClients}
                      onChangeAction={(event, selectedOptions) => {
                        // apenas atualiza a VARIÁVEL; o useEffect cuida do fetch
                        setSelectedClients(selectedOptions || []);
                      }}
                      onInputChange={(inputValue: string) => {
                        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
                        debounceTimeout.current = setTimeout(() => {
                          fetchClients(inputValue || ''); // carrega/filtra clientes
                        }, 400);
                      }}
                    />
                  )}
                </Grid>

                {/* FollowUps */}
                <Grid item xs={4}>
                  {isLoadingFollowups ? (
                    <CircularProgress size={24} />
                  ) : (
                    <MultiSelectStyled
                      options={followups}
                      placeholder="Filtre pelo número do followup"
                      label={t('Selecione os FollowUps')}
                      controlledValues={selectedFollowups}
                      onChangeAction={(event, selectedOptions) => setSelectedFollowups(selectedOptions || [])}
                    />
                  )}
                </Grid>

                <Grid item xs={12} textAlign="right">
                  <Button type="submit" color="secondary" variant="contained" disabled={submitting}>
                    {submitting ? <CircularProgress size={'24px'} /> : 'Exportar fatura'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          );
        }}
      </Form>
    </PageLayout>
  );
};

export default OcrExportPage;
